import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Box,
} from "@mui/material";
import IosShareRounded from "@mui/icons-material/IosShareRounded";
import AddBoxOutlined from "@mui/icons-material/AddBoxOutlined";
import CheckRounded from "@mui/icons-material/CheckRounded";
import InstallMobileRounded from "@mui/icons-material/InstallMobileRounded";
import { useInstallApp } from "hooks/useInstallApp";
import { useTranslation } from "react-i18next";

/**
 * Walks the user through putting HealthDesk on their home screen.
 *
 * On Chromium this is a single button, because the browser gives us an install
 * event to fire. Everywhere else the browser will only install from a menu the
 * user opens themselves, so all we can do is say which menu and what to tap.
 *
 * On iOS, that "everywhere else" is Safari only — Apple never gave other iOS
 * browsers (Chrome, Brave, Firefox, Edge, ...) the ability to add a real
 * standalone app to the home screen, even though they all run on WebKit. So
 * "ios-chrome" and "ios-other" get told to switch to Safari, not fake steps.
 */

const NON_SAFARI_IOS_METHODS = new Set(["ios-chrome", "ios-other"]);

// Each step's text is a "home" namespace key, translated at render.
const STEPS = {
  "ios-safari": [
    {
      icon: <IosShareRounded />,
      textKey: "installApp.steps.iosShare",
    },
    {
      icon: <AddBoxOutlined />,
      textKey: "installApp.steps.iosAdd",
    },
    { icon: <CheckRounded />, textKey: "installApp.steps.iosConfirm" },
  ],
  "macos-safari": [
    {
      icon: <IosShareRounded />,
      textKey: "installApp.steps.macShare",
    },
    { icon: <AddBoxOutlined />, textKey: "installApp.steps.macAdd" },
    { icon: <CheckRounded />, textKey: "installApp.steps.macConfirm" },
  ],
};

const InstallAppDialog = ({ open, onClose }) => {
  const { t } = useTranslation("home");
  const { method, canPromptDirectly, promptInstall } = useInstallApp();
  const needsSafari = NON_SAFARI_IOS_METHODS.has(method);
  const steps = STEPS[method];

  // iOS gives an installed app its own storage, separate from the browser's, so
  // the signed-in session does not come across and the first launch starts at
  // the login screen. Saying so up front stops it reading as a bug. Android and
  // desktop share storage with the browser and stay signed in.
  const signsOutOnFirstOpen = method.startsWith("ios");

  const handleInstall = async () => {
    await promptInstall();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("installApp.title")}</DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t("installApp.intro")}
        </DialogContentText>

        {signsOutOnFirstOpen && (
          <DialogContentText sx={{ pt: 1 }} color="text.secondary">
            {t("installApp.signInOnce")}
          </DialogContentText>
        )}

        {canPromptDirectly && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 3, pb: 1 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<InstallMobileRounded />}
              onClick={handleInstall}
            >
              {t("installApp.install")}
            </Button>
          </Box>
        )}

        {!canPromptDirectly && needsSafari && (
          <DialogContentText sx={{ pt: 2 }}>
            {t("installApp.needsSafari")}
          </DialogContentText>
        )}

        {!canPromptDirectly && !needsSafari && steps && (
          <List>
            {steps.map((step) => (
              <ListItem key={step.textKey} disableGutters>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "primary.main" }}>{step.icon}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={t(step.textKey)} />
              </ListItem>
            ))}
          </List>
        )}

        {!canPromptDirectly && !needsSafari && !steps && (
          <DialogContentText sx={{ pt: 2 }} color="text.secondary">
            {t("installApp.fallback")}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("common.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstallAppDialog;
